import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Building2, Trash2, Search, Mail, Phone, ShieldCheck, Eye, Edit2, X } from 'lucide-react'; 
import AdminLayout from '../layouts/AdminLayout';
import PageTitle from '../components/ui/PageTitle';

export default function ManageEmployers() {
    const [employers, setEmployers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [message, setMessage] = useState('');
    
    const navigate = useNavigate();
    const token = localStorage.getItem('admin_token') || localStorage.getItem('token');

    // 🌟 Modal/Popup States for Updating Mobile Number
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedEmployer, setSelectedEmployer] = useState(null);
    const [newPhone, setNewPhone] = useState('');
    const [updatingPhone, setUpdatingPhone] = useState(false);

    const fetchEmployers = async () => {
        setLoading(true);
        try {
            const response = await api.get('/admin/employers', { 
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.data.status === 'success') {
                setEmployers(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching employers:', error);
            try {
                const fbResponse = await api.get('/employers', { 
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (fbResponse.data.status === 'success') setEmployers(fbResponse.data.data);
            } catch(e) {}
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEmployers();
    }, []);

    const handleToggleStatus = async (emp) => {
        const newStatus = emp.status === 'active' ? 'inactive' : 'active';
        
        try {
            await api.post(`/admin/employers/${emp.id}/status`, 
                { status: newStatus },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            fetchEmployers();
            setMessage(`Employer status changed to ${newStatus}!`);
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error('Error toggling employer status:', error);
            alert('Failed to change status.');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this account?')) return;

        try {
            await api.delete(`/admin/employers/${id}`, { 
                headers: { 'Authorization': `Bearer ${token}` }
            });
            setMessage('Account deleted successfully!');
            fetchEmployers();
            setTimeout(() => setMessage(''), 3000);
        } catch (error) {
            console.error('Error deleting account:', error);
            alert('Failed to delete account.');
        }
    };

    // 🌟 Open Edit Modal (Email is fixed, only Phone opens)
    const openEditModal = (emp) => {
        setSelectedEmployer(emp);
        setNewPhone(emp.phone || '');
        setIsEditModalOpen(true);
    };

    // 🌟 Handle Update Submit
    const handleUpdateMobile = async (e) => {
        e.preventDefault();
        
        if (newPhone.length !== 10) {
            alert('Phone number must be exactly 10 digits.');
            return;
        }

        setUpdatingPhone(true);
        try {
            const response = await api.put(`/admin/employers/${selectedEmployer.id}/update-mobile`, 
                { phone: newPhone },
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (response.data.success) {
                setMessage('Mobile number updated successfully!');
                setIsEditModalOpen(false);
                fetchEmployers(); 
                setTimeout(() => setMessage(''), 3000);
            } else {
                alert(response.data.message || 'Failed to update number.');
            }
        } catch (error) {
            console.error('Error updating phone:', error);
            alert(error.response?.data?.message || 'Error updating mobile number.');
        } finally {
            setUpdatingPhone(false);
        }
    };

    const filteredEmployers = employers.filter(emp => 
        (emp.company_name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (emp.name?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (emp.email?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (emp.phone?.includes(searchTerm))
    );

    return (
        <AdminLayout>
            <div className="max-w-7xl mx-auto space-y-8 relative">
                
                <div className="mb-2">
                    <PageTitle 
                        title="Manage Employers / Employers" 
                        subtitle="View, manage, and edit registered accounts." 
                    />
                </div>

                {message && (
                    <div className="p-4 bg-green-50 border border-green-200 text-green-700 rounded-xl text-sm font-medium flex items-center shadow-sm">
                        {message}
                    </div>
                )}

                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                    <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-between items-center gap-4 bg-gray-50/50">
                        <div className="flex items-center gap-2">
                            <Building2 className="text-[#FED547]" size={22} />
                            <h3 className="font-bold text-gray-900 dark:text-white text-lg">Registered Accounts ({employers.length})</h3>
                        </div>
                        <div className="relative w-full sm:w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                            <input 
                                type="text" 
                                placeholder="Search by name, email or phone..." 
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-full focus:outline-none focus:ring-1 focus:ring-[#FED547] focus:border-[#FED547] dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                            />
                        </div>
                    </div>

                    {loading ? (
                        <div className="p-12 text-center text-gray-500 text-sm">Loading data...</div>
                    ) : filteredEmployers.length === 0 ? (
                        <div className="p-12 text-center text-gray-500 dark:text-gray-400 text-sm">
                            No accounts found matching your search.
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-[#FED547] text-black text-xs uppercase tracking-wider font-bold">
                                        <th className="py-4 px-6 w-16">S.NO</th>
                                        <th className="py-4 px-6">COMPANY NAME</th>
                                        <th className="py-4 px-6">CONTACT PERSON</th>
                                        <th className="py-4 px-6">CONTACT INFO</th>
                                        <th className="py-4 px-6">ROLE ASSIGNED</th>
                                        <th className="py-4 px-6">STATUS</th>
                                        <th className="py-4 px-6 text-center">ACTIONS</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-gray-700 text-sm text-gray-700 dark:text-gray-300">
                                    {filteredEmployers.map((emp, index) => (
                                        <tr key={emp.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                                            <td className="py-4 px-6 font-semibold">{index + 1}</td>
                                            <td className="py-4 px-6 font-bold text-gray-900 dark:text-white">
                                                {emp.company_name}
                                            </td>
                                            <td className="py-4 px-6">
                                                <div className="font-semibold text-gray-800 dark:text-gray-200">{emp.name}</div>
                                            </td>
                                            <td className="py-4 px-6 space-y-2">
                                                {/* Email Read-only fix id */}
                                                <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                                                    <Mail size={13} className="text-blue-500" /> {emp.email || 'N/A'}
                                                </div>
                                                {/* Editable Phone */}
                                                <div className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400">
                                                    <Phone size={13} className="text-emerald-500" /> {emp.phone || 'N/A'}
                                                    <button onClick={() => openEditModal(emp)} className="ml-1 text-gray-400 hover:text-blue-600 transition-colors" title="Edit Mobile Number">
                                                        <Edit2 size={13} />
                                                    </button>
                                                </div>
                                            </td>
                                            
                                            <td className="py-4 px-6">
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-100 dark:bg-yellow-950/40 text-yellow-800 dark:text-yellow-400 text-xs font-bold rounded-lg">
                                                    <ShieldCheck size={13} /> {emp.role || 'Employer'}
                                                </span>
                                            </td>
                                            
                                            <td className="py-4 px-6">
                                                <div className="flex items-center gap-3">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleToggleStatus(emp)}
                                                        className={`relative inline-flex h-5 w-10 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${
                                                            emp.status === 'active' ? 'bg-green-500' : 'bg-gray-300'
                                                        }`}
                                                    >
                                                        <span
                                                            className={`pointer-events-none inline-block h-3.5 w-3.5 mt-0.5 ml-0.5 transform rounded-full bg-white transition duration-200 ease-in-out ${
                                                                emp.status === 'active' ? 'translate-x-5' : 'translate-x-0'
                                                            }`}
                                                        />
                                                    </button>
                                                    <span className={`text-xs font-bold uppercase ${emp.status === 'active' ? 'text-green-600' : 'text-red-500'}`}>
                                                        {emp.status || 'inactive'}
                                                    </span>
                                                </div>
                                            </td>

                                            <td className="py-4 px-6">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button 
                                                        onClick={() => navigate(`/admin/employers/${emp.id}`)}
                                                        className="p-2 bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400 rounded-xl hover:bg-blue-100 transition-colors inline-flex items-center justify-center"
                                                        title="View Details"
                                                    >
                                                        <Eye size={16} />
                                                    </button>
                                                    
                                                    <button 
                                                        onClick={() => openEditModal(emp)}
                                                        className="p-2 bg-emerald-50 text-emerald-600 dark:bg-emerald-900/30 dark:text-emerald-400 rounded-xl hover:bg-emerald-100 transition-colors inline-flex items-center justify-center"
                                                        title="Edit Mobile Number"
                                                    >
                                                        <Edit2 size={16} />
                                                    </button>

                                                    <button 
                                                        onClick={() => handleDelete(emp.id)}
                                                        className="p-2 bg-red-50 text-red-600 dark:bg-red-900/30 dark:text-red-400 rounded-xl hover:bg-red-100 transition-colors inline-flex items-center justify-center"
                                                        title="Delete Account"
                                                    >
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* 🌟 EDIT MOBILE NUMBER MODAL */}
                {isEditModalOpen && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm p-4">
                        <div className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl p-6 animate-in fade-in zoom-in-95 duration-200">
                            
                            <button 
                                onClick={() => setIsEditModalOpen(false)} 
                                className="absolute top-4 right-4 p-1.5 bg-gray-100 rounded-full text-gray-500 hover:bg-red-50 hover:text-red-500 transition-colors"
                            >
                                <X size={18} />
                            </button>

                            <div className="mb-5 text-center">
                                <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-3">
                                    <Phone className="text-blue-500" size={24} />
                                </div>
                                <h3 className="text-xl font-extrabold text-gray-900">Update Mobile</h3>
                                <p className="text-sm font-medium text-gray-500 mt-1">
                                    {selectedEmployer?.name || 'Employer/Employer'}
                                </p>
                            </div>

                            <form onSubmit={handleUpdateMobile} className="space-y-4">
                                {/* Email Field - Read Only */}
                                <div>
                                    <label className="block text-xs font-extrabold text-gray-500 mb-1.5 uppercase tracking-wider">
                                        Email ID (Identification)
                                    </label>
                                    <input 
                                        type="email" 
                                        value={selectedEmployer?.email || 'N/A'} 
                                        readOnly
                                        className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-bold text-gray-500 bg-gray-100 cursor-not-allowed" 
                                    />
                                </div>

                                {/* Phone Field - Editable */}
                                <div>
                                    <label className="block text-xs font-extrabold text-gray-500 mb-1.5 uppercase tracking-wider">
                                        New Phone Number
                                    </label>
                                    <input 
                                        type="tel" 
                                        value={newPhone} 
                                        onChange={(e) => setNewPhone(e.target.value.replace(/\D/g, '').slice(0, 10))} 
                                        required 
                                        placeholder="Enter 10-digit number"
                                        className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm font-bold text-gray-900 focus:outline-none focus:border-blue-500 bg-gray-50 focus:bg-white transition-colors" 
                                    />
                                    {newPhone.length > 0 && newPhone.length < 10 && (
                                        <span className="text-[10px] text-red-500 font-bold mt-1 block">Number must be 10 digits.</span>
                                    )}
                                </div>

                                <button 
                                    type="submit" 
                                    disabled={updatingPhone || newPhone.length !== 10} 
                                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-extrabold py-3 rounded-xl transition-all shadow-md disabled:opacity-50 disabled:cursor-not-allowed mt-2"
                                >
                                    {updatingPhone ? 'Updating...' : 'Save Mobile Number'}
                                </button>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}